import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, LogOut, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { RoamieTab } from "@/components/admin/RoamieTab";
import { EngagementTab } from "@/components/admin/EngagementTab";

const lines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const csv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

// itinerary line format: DAY 1 | Title | item, item, item
const parseItinerary = (text) =>
  lines(text).map((line) => {
    const [day = "", title = "", items = ""] = line.split("|").map((x) => x.trim());
    return { day, title, items: csv(items) };
  });
const itineraryToText = (it) =>
  (it || []).map((d) => `${d.day} | ${d.title} | ${(d.items || []).join(", ")}`).join("\n");

// faq line format: Question = Answer
const parseFaqs = (text) =>
  lines(text).map((line) => {
    const [q = "", a = ""] = line.split("=").map((x) => x.trim());
    return { q, a };
  }).filter((f) => f.q);
const faqsToText = (faqs) => (faqs || []).map((f) => `${f.q} = ${f.a}`).join("\n");

const emptyTrip = {
  title: "", slug: "", destination: "", category: "weekend", region: "usa",
  duration: "", dates: "", date_label: "", group_size: "Max 12 travelers", capacity: 12,
  age_min: 18, age_max: 45, tagline: "", hook: "", description: "", highlights: "",
  image: "", gallery: "", featured: false, status: "coming_soon", published: true,
  collection: "future", drop_number: "", meeting_location: "", trip_style: "", activity_level: "",
  itinerary: "", stay_name: "", stay_room_config: "", stay_amenities: "", stay_location: "",
  included: "", excluded: "", faqs: "", who_for: "", host_id: "",
  start_date: "", end_date: "", booking_open_date: "", booking_deadline: "",
  deposit_deadline: "", final_payment_deadline: "",
  solo_policy: "solo_friends",
  transportation_info: "", what_to_bring: "", important_info: "",
  recommended_airport: "", meeting_city: "", meeting_address: "", meeting_time: "",
  departure_point: "", airport_transfer_info: "", price_label: "",
  approval_required: true, payment_flow: "approval_first",
  deposit: "", cancellation_info: "", seo_title: "", seo_description: "",
  basic_price: "", standard_price: "", premium_price: "",
  basic_includes: "", standard_includes: "", premium_includes: "",
};

const tripToForm = (t) => ({
  title: t.title || "", slug: t.slug || "", destination: t.destination || "",
  category: t.category || "weekend", region: t.region || "usa",
  duration: t.duration || "", dates: (t.dates || []).join(", "), date_label: t.date_label || "",
  group_size: t.group_size || "", capacity: t.capacity ?? 12,
  age_min: t.age_min ?? 18, age_max: t.age_max ?? 45,
  tagline: t.tagline || "", hook: t.hook || "", description: t.description || "",
  highlights: (t.highlights || []).join("\n"), image: t.image || "",
  gallery: (t.gallery || []).join(", "), featured: !!t.featured,
  status: t.status || "coming_soon", published: t.published !== false,
  collection: t.collection || "future",
  drop_number: t.drop_number || "", meeting_location: t.meeting_location || "",
  trip_style: t.trip_style || "", activity_level: t.activity_level || "",
  itinerary: itineraryToText(t.itinerary),
  stay_name: t.stay?.name || "", stay_room_config: t.stay?.room_config || "",
  stay_amenities: (t.stay?.amenities || []).join(", "), stay_location: t.stay?.location || "",
  included: (t.included || []).join("\n"), excluded: (t.excluded || []).join("\n"),
  faqs: faqsToText(t.faqs), who_for: (t.who_for || []).join("\n"),
  host_id: t.host_id || "", deposit: t.deposit ?? "",
  start_date: t.start_date || "", end_date: t.end_date || "",
  booking_open_date: t.booking_open_date || "", booking_deadline: t.booking_deadline || "",
  deposit_deadline: t.deposit_deadline || "", final_payment_deadline: t.final_payment_deadline || "",
  solo_policy: t.solo_policy || "solo_friends",
  transportation_info: t.transportation_info || "",
  what_to_bring: (t.what_to_bring || []).join("\n"), important_info: t.important_info || "",
  recommended_airport: t.recommended_airport || "", meeting_city: t.meeting_city || "",
  meeting_address: t.meeting_address || "", meeting_time: t.meeting_time || "",
  departure_point: t.departure_point || "", airport_transfer_info: t.airport_transfer_info || "",
  price_label: t.price_label || "",
  approval_required: t.approval_required !== false, payment_flow: t.payment_flow || "approval_first",
  cancellation_info: t.cancellation_info || "",
  seo_title: t.seo_title || "", seo_description: t.seo_description || "",
  basic_price: t.tiers?.basic?.price ?? "", standard_price: t.tiers?.standard?.price ?? "",
  premium_price: t.tiers?.premium?.price ?? "",
  basic_includes: (t.tiers?.basic?.includes || []).join("\n"),
  standard_includes: (t.tiers?.standard?.includes || []).join("\n"),
  premium_includes: (t.tiers?.premium?.includes || []).join("\n"),
});

const formToTrip = (f) => ({
  title: f.title, slug: f.slug, destination: f.destination, category: f.category, region: f.region,
  duration: f.duration, dates: csv(f.dates), date_label: f.date_label,
  group_size: f.group_size, capacity: Number(f.capacity) || 12,
  age_min: Number(f.age_min) || 18, age_max: Number(f.age_max) || 45,
  tagline: f.tagline, hook: f.hook, description: f.description,
  highlights: lines(f.highlights), image: f.image, gallery: csv(f.gallery),
  featured: f.featured, status: f.status, published: f.published, collection: f.collection,
  drop_number: f.drop_number, meeting_location: f.meeting_location,
  trip_style: f.trip_style, activity_level: f.activity_level,
  itinerary: parseItinerary(f.itinerary),
  stay: { name: f.stay_name, room_config: f.stay_room_config, amenities: csv(f.stay_amenities), location: f.stay_location, images: [] },
  included: lines(f.included), excluded: lines(f.excluded), faqs: parseFaqs(f.faqs),
  who_for: lines(f.who_for), host_id: f.host_id === "none" ? "" : f.host_id,
  start_date: f.start_date, end_date: f.end_date,
  booking_open_date: f.booking_open_date, booking_deadline: f.booking_deadline,
  deposit_deadline: f.deposit_deadline, final_payment_deadline: f.final_payment_deadline,
  solo_policy: f.solo_policy,
  transportation_info: f.transportation_info, what_to_bring: lines(f.what_to_bring),
  important_info: f.important_info,
  recommended_airport: f.recommended_airport, meeting_city: f.meeting_city,
  meeting_address: f.meeting_address, meeting_time: f.meeting_time,
  departure_point: f.departure_point, airport_transfer_info: f.airport_transfer_info,
  price_label: f.price_label,
  approval_required: f.approval_required, payment_flow: f.payment_flow,
  deposit: f.deposit === "" ? null : Number(f.deposit),
  cancellation_info: f.cancellation_info, seo_title: f.seo_title, seo_description: f.seo_description,
  tiers: f.basic_price !== "" ? {
    basic: { name: "Basic", tagline: "Stay Only", price: Number(f.basic_price) || 0, recommended: false, includes: lines(f.basic_includes) },
    standard: { name: "Standard", tagline: "Stay + Food", price: Number(f.standard_price) || 0, recommended: true, includes: lines(f.standard_includes) },
    premium: { name: "Premium", tagline: "All-Inclusive", price: Number(f.premium_price) || 0, recommended: false, includes: lines(f.premium_includes) },
  } : {},
});

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs font-bold uppercase tracking-widest">{label}</Label>
    {children}
  </div>
);

export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem("roamies_admin_token");
      navigate("/admin/login");
    });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("roamies_admin_token");
    navigate("/admin/login");
  };

  if (!user) {
    return <div className="min-h-screen bg-ink text-cream flex items-center justify-center font-semibold" data-testid="admin-loading">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-ink text-cream noise-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <p className="font-display text-xl tracking-tight">ROAMIES<span className="text-flame">.</span> <span className="text-cream/40 text-sm font-sans font-bold uppercase tracking-widest ml-2">Admin</span></p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-cream/60 hidden sm:block" data-testid="admin-user-email">{user.email}</span>
            <a href="/" className="text-xs font-bold uppercase tracking-widest text-cream/60 hover:text-flame transition-colors" data-testid="admin-view-site-link">View site</a>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-cream/60 hover:text-flame transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Tabs defaultValue="overview">
          <TabsList className="rounded-none bg-ink/5 flex-wrap h-auto" data-testid="admin-tabs">
            {["overview", "trips", "events", "applications", "waitlist", "travelers", "screening", "survey", "roamie", "engagement", "bookings", "payments", "payouts", "refunds", "promos", "hosts", "testimonials", "votes", "contacts", "newsletter", "content", "settings"].map((tab) => (
              <TabsTrigger key={tab} value={tab} data-testid={`admin-tab-${tab}`}
                className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-cream capitalize">
                {tab === "events" ? "Roamies IRL" : tab === "promos" ? "Promo Codes" : tab}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview"><OverviewTab /></TabsContent>
          <TabsContent value="trips"><TripsTab /></TabsContent>
          <TabsContent value="events"><EventsTab /></TabsContent>
          <TabsContent value="applications"><ApplicationsTab /></TabsContent>
          <TabsContent value="waitlist"><WaitlistTab /></TabsContent>
          <TabsContent value="travelers"><TravelersTab /></TabsContent>
          <TabsContent value="screening"><ScreeningTab /></TabsContent>
          <TabsContent value="survey"><SurveyTab /></TabsContent>
          <TabsContent value="roamie"><RoamieTab /></TabsContent>
          <TabsContent value="engagement"><EngagementTab /></TabsContent>
          <TabsContent value="bookings"><BookingsTab /></TabsContent>
          <TabsContent value="payments"><FinanceTab kind="payments" /></TabsContent>
          <TabsContent value="payouts"><FinanceTab kind="payouts" /></TabsContent>
          <TabsContent value="refunds"><FinanceTab kind="refunds" /></TabsContent>
          <TabsContent value="promos"><PromosTab /></TabsContent>
          <TabsContent value="hosts"><HostsTab /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
          <TabsContent value="votes"><VotesTab /></TabsContent>
          <TabsContent value="contacts"><ContactsTab /></TabsContent>
          <TabsContent value="newsletter"><SubscribersTab /></TabsContent>
          <TabsContent value="content"><ContentTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  if (!stats) return <p className="mt-6 text-ink/50 font-semibold" data-testid="overview-loading">Loading stats...</p>;

  const cards = [
    { label: "Total waitlist", value: stats.total_waitlist },
    { label: "Upcoming trips", value: stats.upcoming_trips },
    { label: "Upcoming IRL events", value: stats.upcoming_events },
    { label: "Newsletter subs", value: stats.subscribers },
    { label: "Contact messages", value: stats.contacts },
  ];

  return (
    <div className="mt-6" data-testid="admin-overview-tab">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-black/10 p-6" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="font-display text-4xl">{c.value}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-ink/50 mt-2">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="bg-white border border-black/10 p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-4">Waitlist by trip</p>
          {stats.waitlist_by_trip.length === 0 ? (
            <p className="text-sm text-ink/50">No signups yet.</p>
          ) : (
            stats.waitlist_by_trip.map((r) => (
              <div key={r.trip} className="flex justify-between py-2 border-b border-black/5 text-sm">
                <span className="font-semibold">{r.trip}</span><span className="font-display text-lg">{r.count}</span>
              </div>
            ))
          )}
        </div>
        <div className="bg-white border border-black/10 p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-4">Top destination votes</p>
          {stats.top_votes.length === 0 ? (
            <p className="text-sm text-ink/50">No votes yet.</p>
          ) : (
            stats.top_votes.map((r) => (
              <div key={r.trip_slug} className="flex justify-between py-2 border-b border-black/5 text-sm">
                <span className="font-semibold">{r.trip_slug}</span><span className="font-display text-lg">{r.count}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function TripsTab() {
  const [trips, setTrips] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTrip);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/trips").then((r) => setTrips(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/hosts").then((r) => setHosts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyTrip); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm(tripToForm(t)); setDialogOpen(true); };

  const onImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = formToTrip(form);
    try {
      if (editing) {
        await api.put(`/admin/trips/${editing.id}`, payload);
        toast.success("Trip updated");
      } else {
        await api.post("/admin/trips", payload);
        toast.success("Trip added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/trips/${t.id}`);
      toast.success("Trip deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-6" data-testid="admin-trips-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm font-semibold text-ink/60">{trips.length} trips (drops included)</p>
        <Button onClick={openAdd} data-testid="admin-add-trip-btn"
          className="bg-flame hover:bg-flame/90 text-white rounded-none font-bold uppercase tracking-widest text-xs">
          <Plus className="w-4 h-4 mr-2" /> Add Trip
        </Button>
      </div>

      <div className="bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left">
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Trip</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Status</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Dates</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Featured</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {trips.map((t) => (
              <tr key={t.id} className="border-b border-black/5 hover:bg-cream/60" data-testid={`admin-trip-row-${t.slug}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {t.image && <img src={t.image} alt="" className="w-12 h-12 object-cover border border-black/10" />}
                    <div>
                      <p className="font-bold">{t.drop_number ? `${t.drop_number} · ` : ""}{t.title}</p>
                      <p className="text-xs text-ink/50">{t.destination} · {t.duration}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-ink/5 px-2 py-1">
                    {(t.status || "coming_soon").replace(/_/g, " ")}{t.published === false ? " · unpublished" : ""}
                  </span>
                </td>
                <td className="p-4 text-xs font-semibold">{t.date_label || (t.dates || []).join(", ") || "TBA"}</td>
                <td className="p-4">{t.featured && <span className="text-[10px] font-bold uppercase tracking-widest bg-flame text-white px-2 py-1">Drop</span>}</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(t)} data-testid={`admin-edit-trip-${t.slug}`} className="p-2 text-ink/50 hover:text-ink transition-colors" aria-label="Edit trip"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(t)} data-testid={`admin-delete-trip-${t.slug}`} className="p-2 text-ink/50 hover:text-red-600 transition-colors" aria-label="Delete trip"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="admin-trip-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">{editing ? "Edit Trip" : "Add Trip"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-6 mt-4" data-testid="admin-trip-form">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame border-b border-black/10 pb-2">Basics</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title"><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="trip-form-title" className="rounded-none" /></Field>
              <Field label="Slug (auto if blank)"><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="trip-form-slug" className="rounded-none" /></Field>
              <Field label="Destination"><Input required value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="trip-form-destination" className="rounded-none" placeholder="City, State / Country" /></Field>
              <Field label="Drop number (optional)"><Input value={form.drop_number} onChange={(e) => setForm({ ...form, drop_number: e.target.value })} data-testid="trip-form-drop-number" className="rounded-none" placeholder="DROP 001" /></Field>
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="trip-form-category" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekend">Weekend Getaway</SelectItem>
                    <SelectItem value="flagship">Flagship International</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Region">
                <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                  <SelectTrigger data-testid="trip-form-region" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usa">USA</SelectItem>
                    <SelectItem value="international">International</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="trip-form-status" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["draft", "coming_soon", "waitlist_open", "booking_open", "sold_out", "completed"].map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Catalog section">
                <Select value={form.collection} onValueChange={(v) => setForm({ ...form, collection: v })}>
                  <SelectTrigger data-testid="trip-form-collection" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coming_soon">Coming Soon (launch trips)</SelectItem>
                    <SelectItem value="waitlist">Waitlist</SelectItem>
                    <SelectItem value="future">Future Adventures</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date label"><Input value={form.date_label} onChange={(e) => setForm({ ...form, date_label: e.target.value })} data-testid="trip-form-date-label" className="rounded-none" placeholder="October 2026" /></Field>
              <Field label="Start date"><Input value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="trip-form-start-date" className="rounded-none" placeholder="Oct 9, 2026" /></Field>
              <Field label="End date"><Input value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="trip-form-end-date" className="rounded-none" placeholder="Oct 12, 2026" /></Field>
              <Field label="Booking opens"><Input value={form.booking_open_date} onChange={(e) => setForm({ ...form, booking_open_date: e.target.value })} data-testid="trip-form-booking-open" className="rounded-none" placeholder="Sep 1, 2026" /></Field>
              <Field label="Booking deadline"><Input value={form.booking_deadline} onChange={(e) => setForm({ ...form, booking_deadline: e.target.value })} data-testid="trip-form-booking-deadline" className="rounded-none" /></Field>
              <Field label="Deposit deadline"><Input value={form.deposit_deadline} onChange={(e) => setForm({ ...form, deposit_deadline: e.target.value })} data-testid="trip-form-deposit-deadline" className="rounded-none" /></Field>
              <Field label="Final payment deadline"><Input value={form.final_payment_deadline} onChange={(e) => setForm({ ...form, final_payment_deadline: e.target.value })} data-testid="trip-form-final-payment" className="rounded-none" /></Field>
              <Field label="Exact dates (comma separated)"><Input value={form.dates} onChange={(e) => setForm({ ...form, dates: e.target.value })} data-testid="trip-form-dates" className="rounded-none" placeholder="Oct 9–12, 2026" /></Field>
              <Field label="Duration"><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="trip-form-duration" className="rounded-none" placeholder="3 Nights" /></Field>
              <Field label="Group size label"><Input value={form.group_size} onChange={(e) => setForm({ ...form, group_size: e.target.value })} data-testid="trip-form-group-size" className="rounded-none" /></Field>
              <Field label="Max travelers"><Input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="trip-form-capacity" className="rounded-none" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Age min"><Input type="number" value={form.age_min} onChange={(e) => setForm({ ...form, age_min: e.target.value })} data-testid="trip-form-age-min" className="rounded-none" /></Field>
                <Field label="Age max"><Input type="number" value={form.age_max} onChange={(e) => setForm({ ...form, age_max: e.target.value })} data-testid="trip-form-age-max" className="rounded-none" /></Field>
              </div>
              <Field label="Who can join">
                <Select value={form.solo_policy} onValueChange={(v) => setForm({ ...form, solo_policy: v })}>
                  <SelectTrigger data-testid="trip-form-solo-policy" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solo_friends">Solo travelers + friends allowed</SelectItem>
                    <SelectItem value="solo_only">Solo travelers only</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Trip style"><Input value={form.trip_style} onChange={(e) => setForm({ ...form, trip_style: e.target.value })} data-testid="trip-form-trip-style" className="rounded-none" placeholder="Outdoors + Social" /></Field>
              <Field label="Activity level"><Input value={form.activity_level} onChange={(e) => setForm({ ...form, activity_level: e.target.value })} data-testid="trip-form-activity-level" className="rounded-none" placeholder="Moderate" /></Field>
              <Field label="Meeting location"><Input value={form.meeting_location} onChange={(e) => setForm({ ...form, meeting_location: e.target.value })} data-testid="trip-form-meeting-location" className="rounded-none" placeholder="Announced before booking opens" /></Field>
              <Field label="Recommended airport"><Input value={form.recommended_airport} onChange={(e) => setForm({ ...form, recommended_airport: e.target.value })} data-testid="trip-form-airport" className="rounded-none" placeholder="TYS — Knoxville" /></Field>
              <Field label="Meeting city"><Input value={form.meeting_city} onChange={(e) => setForm({ ...form, meeting_city: e.target.value })} data-testid="trip-form-meeting-city" className="rounded-none" /></Field>
              <Field label="Meeting address"><Input value={form.meeting_address} onChange={(e) => setForm({ ...form, meeting_address: e.target.value })} data-testid="trip-form-meeting-address" className="rounded-none" /></Field>
              <Field label="Meeting time"><Input value={form.meeting_time} onChange={(e) => setForm({ ...form, meeting_time: e.target.value })} data-testid="trip-form-meeting-time" className="rounded-none" placeholder="4:00 PM" /></Field>
              <Field label="Departure / drop-off point"><Input value={form.departure_point} onChange={(e) => setForm({ ...form, departure_point: e.target.value })} data-testid="trip-form-departure-point" className="rounded-none" /></Field>
              <Field label="Airport transfer info"><Input value={form.airport_transfer_info} onChange={(e) => setForm({ ...form, airport_transfer_info: e.target.value })} data-testid="trip-form-airport-transfer" className="rounded-none" /></Field>
              <Field label="Host">
                <Select value={form.host_id || "none"} onValueChange={(v) => setForm({ ...form, host_id: v })}>
                  <SelectTrigger data-testid="trip-form-host" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No host assigned</SelectItem>
                    {hosts.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="flex flex-wrap gap-8">
              <div className="flex items-center gap-3">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="trip-form-featured" />
                <Label className="text-xs font-bold uppercase tracking-widest">Featured drop</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="trip-form-published" />
                <Label className="text-xs font-bold uppercase tracking-widest">Published</Label>
              </div>
            </div>

            <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame border-b border-black/10 pb-2">Story</p>
            <Field label="Hook (one-liner)"><Input value={form.hook} onChange={(e) => setForm({ ...form, hook: e.target.value })} data-testid="trip-form-hook" className="rounded-none" placeholder="12 strangers. One unforgettable fall weekend." /></Field>
            <Field label="Tagline"><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="trip-form-tagline" className="rounded-none" /></Field>
            <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="trip-form-description" className="rounded-none min-h-[100px]" /></Field>
            <Field label="Why you'll love it (one per line)"><Textarea value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} data-testid="trip-form-highlights" className="rounded-none min-h-[90px]" /></Field>
            <Field label="Itinerary (DAY 1 | Title | item, item, item — one day per line)">
              <Textarea value={form.itinerary} onChange={(e) => setForm({ ...form, itinerary: e.target.value })} data-testid="trip-form-itinerary" className="rounded-none min-h-[110px] text-xs font-mono" placeholder={"DAY 1 | Strangers → Roamies | Arrival, Welcome activity, Dinner, Bonfire"} />
            </Field>

            <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame border-b border-black/10 pb-2">Stay & Logistics</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Stay name"><Input value={form.stay_name} onChange={(e) => setForm({ ...form, stay_name: e.target.value })} data-testid="trip-form-stay-name" className="rounded-none" /></Field>
              <Field label="Room configuration"><Input value={form.stay_room_config} onChange={(e) => setForm({ ...form, stay_room_config: e.target.value })} data-testid="trip-form-stay-rooms" className="rounded-none" /></Field>
              <Field label="Amenities (comma separated)"><Input value={form.stay_amenities} onChange={(e) => setForm({ ...form, stay_amenities: e.target.value })} data-testid="trip-form-stay-amenities" className="rounded-none" /></Field>
              <Field label="General area"><Input value={form.stay_location} onChange={(e) => setForm({ ...form, stay_location: e.target.value })} data-testid="trip-form-stay-location" className="rounded-none" /></Field>
            </div>
            <Field label="What's included (one per line)"><Textarea value={form.included} onChange={(e) => setForm({ ...form, included: e.target.value })} data-testid="trip-form-included" className="rounded-none min-h-[90px]" /></Field>
            <Field label="What's NOT included (one per line)"><Textarea value={form.excluded} onChange={(e) => setForm({ ...form, excluded: e.target.value })} data-testid="trip-form-excluded" className="rounded-none min-h-[90px]" /></Field>
            <Field label="This trip is for you if... (one per line)"><Textarea value={form.who_for} onChange={(e) => setForm({ ...form, who_for: e.target.value })} data-testid="trip-form-who-for" className="rounded-none min-h-[80px]" /></Field>
            <Field label="FAQs (Question = Answer, one per line)"><Textarea value={form.faqs} onChange={(e) => setForm({ ...form, faqs: e.target.value })} data-testid="trip-form-faqs" className="rounded-none min-h-[80px] text-xs font-mono" /></Field>
            <Field label="Transportation info"><Textarea value={form.transportation_info} onChange={(e) => setForm({ ...form, transportation_info: e.target.value })} data-testid="trip-form-transportation" className="rounded-none min-h-[70px]" /></Field>
            <Field label="What to bring (one per line)"><Textarea value={form.what_to_bring} onChange={(e) => setForm({ ...form, what_to_bring: e.target.value })} data-testid="trip-form-what-to-bring" className="rounded-none min-h-[70px]" /></Field>
            <Field label="Important trip information"><Textarea value={form.important_info} onChange={(e) => setForm({ ...form, important_info: e.target.value })} data-testid="trip-form-important-info" className="rounded-none min-h-[70px]" /></Field>
            <Field label="Cancellation info"><Input value={form.cancellation_info} onChange={(e) => setForm({ ...form, cancellation_info: e.target.value })} data-testid="trip-form-cancellation" className="rounded-none" /></Field>

            <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame border-b border-black/10 pb-2">Media & SEO</p>
            <Field label="Hero image URL or upload">
              <Input value={form.image.startsWith("data:") ? "" : form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="trip-form-image" className="rounded-none" placeholder="https://..." />
              <input type="file" accept="image/*" onChange={onImageFile} data-testid="trip-form-image-upload" className="mt-2 text-xs" />
              {form.image && <img src={form.image} alt="preview" className="mt-2 h-24 object-cover border border-black/10" />}
            </Field>
            <Field label="Gallery image URLs (comma separated)"><Input value={form.gallery} onChange={(e) => setForm({ ...form, gallery: e.target.value })} data-testid="trip-form-gallery" className="rounded-none" /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="SEO title"><Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} data-testid="trip-form-seo-title" className="rounded-none" /></Field>
              <Field label="SEO description"><Input value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} data-testid="trip-form-seo-description" className="rounded-none" /></Field>
            </div>

            <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame border-b border-black/10 pb-2">Pricing (leave blank while pricing is TBA)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Price label (shown when booking open)"><Input value={form.price_label} onChange={(e) => setForm({ ...form, price_label: e.target.value })} data-testid="trip-form-price-label" className="rounded-none" placeholder="$899 per person" /></Field>
              <Field label="Deposit ($, optional)"><Input type="number" min="0" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} data-testid="trip-form-deposit" className="rounded-none" /></Field>
              <Field label="Payment workflow">
                <Select value={form.payment_flow} onValueChange={(v) => setForm({ ...form, payment_flow: v })}>
                  <SelectTrigger data-testid="trip-form-payment-flow" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approval_first">Approval before payment</SelectItem>
                    <SelectItem value="pay_first">Pay first, review after</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="flex items-end gap-3 pb-1">
                <Switch checked={form.approval_required} onCheckedChange={(v) => setForm({ ...form, approval_required: v })} data-testid="trip-form-approval-required" />
                <Label className="text-xs font-bold uppercase tracking-widest">Require application + approval</Label>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {["basic", "standard", "premium"].map((tier) => (
                <div key={tier} className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-widest">{tier}{tier === "standard" ? " (recommended)" : ""}</p>
                  <Input type="number" min="0" placeholder="Price ($)" value={form[`${tier}_price`]}
                    onChange={(e) => setForm({ ...form, [`${tier}_price`]: e.target.value })}
                    data-testid={`trip-form-${tier}-price`} className="rounded-none" />
                  <Textarea placeholder="Included (one per line)" value={form[`${tier}_includes`]}
                    onChange={(e) => setForm({ ...form, [`${tier}_includes`]: e.target.value })}
                    data-testid={`trip-form-${tier}-includes`} className="rounded-none min-h-[100px] text-xs" />
                </div>
              ))}
            </div>

            <Button type="submit" disabled={saving} data-testid="trip-form-save-btn"
              className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Trip"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const emptyEvent = {
  title: "", city: "", event_type: "Dinner", date_label: "", time_label: "",
  age_req: "Ages 18–45", max_attendees: 20, description: "", host_name: "",
  price_label: "Free", status: "coming_soon", image: "", included: "", meeting_instructions: "",
};

function EventsTab() {
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEvent);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/events").then((r) => setEvents(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyEvent); setDialogOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setForm({ ...ev, included: (ev.included || []).join("\n") }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, max_attendees: Number(form.max_attendees) || 20, included: lines(form.included) };
    try {
      if (editing) {
        await api.put(`/admin/events/${editing.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    await api.delete(`/admin/events/${ev.id}`);
    load();
  };

  return (
    <div className="mt-6" data-testid="admin-events-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm font-semibold text-ink/60">{events.length} IRL events</p>
        <Button onClick={openAdd} data-testid="admin-add-event-btn"
          className="bg-flame hover:bg-flame/90 text-white rounded-none font-bold uppercase tracking-widest text-xs">
          <Plus className="w-4 h-4 mr-2" /> Add Event
        </Button>
      </div>
      <div className="bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left">
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Event</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">City</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">When</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Status</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-ink/50 font-semibold" data-testid="events-empty">No IRL events yet. Add the first one.</td></tr>
            )}
            {events.map((ev) => (
              <tr key={ev.id} className="border-b border-black/5 hover:bg-cream/60" data-testid={`admin-event-row-${ev.slug}`}>
                <td className="p-4 font-bold">{ev.title}<span className="block text-xs font-normal text-ink/50">{ev.event_type} · {ev.price_label}</span></td>
                <td className="p-4">{ev.city}</td>
                <td className="p-4 text-xs font-semibold">{ev.date_label || "TBA"}{ev.time_label ? ` · ${ev.time_label}` : ""}</td>
                <td className="p-4"><span className="text-[10px] font-bold uppercase tracking-widest bg-ink/5 px-2 py-1">{(ev.status || "").replace(/_/g, " ")}</span></td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(ev)} data-testid={`admin-edit-event-${ev.slug}`} className="p-2 text-ink/50 hover:text-ink" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(ev)} data-testid={`admin-delete-event-${ev.slug}`} className="p-2 text-ink/50 hover:text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="admin-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">{editing ? "Edit Event" : "Add IRL Event"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-5 mt-4" data-testid="admin-event-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Event name"><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-form-title" className="rounded-none" placeholder="Dinner With Strangers" /></Field>
              <Field label="City"><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="event-form-city" className="rounded-none" placeholder="NYC" /></Field>
              <Field label="Event type">
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                  <SelectTrigger data-testid="event-form-type" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Dinner", "Coffee meetup", "Rooftop social", "Hiking meetup", "Beach day", "Game night", "City exploration", "Food crawl", "Nightlife experience", "Meetup"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="event-form-status" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["coming_soon", "rsvp_open", "sold_out", "completed"].map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date label"><Input value={form.date_label} onChange={(e) => setForm({ ...form, date_label: e.target.value })} data-testid="event-form-date" className="rounded-none" placeholder="Sep 20, 2026" /></Field>
              <Field label="Time"><Input value={form.time_label} onChange={(e) => setForm({ ...form, time_label: e.target.value })} data-testid="event-form-time" className="rounded-none" placeholder="7:00 PM" /></Field>
              <Field label="Age requirement"><Input value={form.age_req} onChange={(e) => setForm({ ...form, age_req: e.target.value })} data-testid="event-form-age" className="rounded-none" /></Field>
              <Field label="Max attendees"><Input type="number" min="1" value={form.max_attendees} onChange={(e) => setForm({ ...form, max_attendees: e.target.value })} data-testid="event-form-max" className="rounded-none" /></Field>
              <Field label="Price label"><Input value={form.price_label} onChange={(e) => setForm({ ...form, price_label: e.target.value })} data-testid="event-form-price" className="rounded-none" placeholder="Free / $15" /></Field>
              <Field label="Host name"><Input value={form.host_name} onChange={(e) => setForm({ ...form, host_name: e.target.value })} data-testid="event-form-host" className="rounded-none" /></Field>
            </div>
            <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-form-description" className="rounded-none min-h-[80px]" /></Field>
            <Field label="Image URL"><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="event-form-image" className="rounded-none" placeholder="https://..." /></Field>
            <Field label="What's included (one per line)"><Textarea value={form.included} onChange={(e) => setForm({ ...form, included: e.target.value })} data-testid="event-form-included" className="rounded-none min-h-[70px]" /></Field>
            <Field label="Meeting instructions"><Textarea value={form.meeting_instructions} onChange={(e) => setForm({ ...form, meeting_instructions: e.target.value })} data-testid="event-form-meeting" className="rounded-none min-h-[70px]" /></Field>
            <Button type="submit" disabled={saving} data-testid="event-form-save-btn"
              className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Event"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WaitlistTab() {
  const [entries, setEntries] = useState([]);
  const [trips, setTrips] = useState([]);
  const [search, setSearch] = useState("");
  const [tripFilter, setTripFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("");

  const load = () => {
    const params = {};
    if (tripFilter !== "all") params.trip = tripFilter;
    if (cityFilter) params.city = cityFilter;
    if (search) params.search = search;
    api.get("/admin/waitlist", { params }).then((r) => setEntries(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, [tripFilter, cityFilter]);
  useEffect(() => { api.get("/admin/trips").then((r) => setTrips(r.data)).catch(() => {}); }, []);

  const patch = async (id, updates) => {
    try {
      const { data } = await api.patch(`/admin/waitlist/${id}`, updates);
      setEntries((list) => list.map((e) => (e.id === id ? data : e)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportCsv = async () => {
    const res = await api.get("/admin/waitlist/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "roamies-waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const cities = [...new Set(entries.map((e) => e.city).filter(Boolean))];

  return (
    <div className="mt-6" data-testid="admin-waitlist-tab">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          onBlur={load} placeholder="Search name or email..." data-testid="waitlist-search"
          className="rounded-none h-10 w-56 border-ink/20" />
        <Select value={tripFilter} onValueChange={setTripFilter}>
          <SelectTrigger data-testid="waitlist-trip-filter" className="rounded-none h-10 w-56 border-ink/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All trips</SelectItem>
            {trips.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={cityFilter || "all"} onValueChange={(v) => setCityFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="waitlist-city-filter" className="rounded-none h-10 w-44 border-ink/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm font-semibold text-ink/60 ml-auto">{entries.length} signups</span>
        <Button onClick={exportCsv} data-testid="admin-export-waitlist-btn"
          className="bg-ink hover:bg-ink/90 text-cream rounded-none font-bold uppercase tracking-widest text-xs">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left">
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Name</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Contact</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">City / Age</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Interested in</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Source</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Contacted</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Notes</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-ink/50 font-semibold" data-testid="waitlist-empty">No waitlist signups yet.</td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-black/5 hover:bg-cream/60" data-testid={`admin-waitlist-row-${e.id}`}>
                <td className="p-4 font-bold">{e.name || `${e.first_name} ${e.last_name}`}</td>
                <td className="p-4 text-xs">{e.email}{e.phone ? <span className="block text-ink/50">{e.phone}</span> : null}{e.instagram ? <span className="block text-flame">{e.instagram}</span> : null}</td>
                <td className="p-4 text-xs">{[e.city, e.country].filter(Boolean).join(", ") || "—"}{e.age ? ` · ${e.age}` : ""}</td>
                <td className="p-4 text-xs">{e.trip_title || "Any"}</td>
                <td className="p-4 text-xs">{e.source || "—"}</td>
                <td className="p-4">
                  <Checkbox checked={!!e.contacted} onCheckedChange={(v) => patch(e.id, { contacted: !!v })}
                    data-testid={`waitlist-contacted-${e.id}`}
                    className="rounded-none data-[state=checked]:bg-flame data-[state=checked]:border-flame" />
                </td>
                <td className="p-4">
                  <input defaultValue={e.notes || ""} onBlur={(ev) => ev.target.value !== (e.notes || "") && patch(e.id, { notes: ev.target.value })}
                    data-testid={`waitlist-notes-${e.id}`} placeholder="Add note..."
                    className="text-xs border border-ink/15 px-2 py-1.5 w-36 rounded-none focus:outline-none focus:border-flame" />
                </td>
                <td className="p-4 text-right">
                  <button onClick={async () => { await api.delete(`/admin/waitlist/${e.id}`); load(); }}
                    data-testid={`admin-delete-waitlist-${e.id}`} className="p-2 text-ink/50 hover:text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HostsTab() {
  const [hosts, setHosts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", bio: "", image: "", social: "" });

  const load = () => api.get("/hosts").then((r) => setHosts(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/hosts/${editing.id}`, form);
        toast.success("Host updated");
      } else {
        await api.post("/admin/hosts", form);
        toast.success("Host added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-6" data-testid="admin-hosts-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm font-semibold text-ink/60">{hosts.length} hosts — assign them to trips in the trip editor</p>
        <Button onClick={() => { setEditing(null); setForm({ name: "", bio: "", image: "", social: "" }); setDialogOpen(true); }}
          data-testid="admin-add-host-btn" className="bg-flame hover:bg-flame/90 text-white rounded-none font-bold uppercase tracking-widest text-xs">
          <Plus className="w-4 h-4 mr-2" /> Add Host
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {hosts.length === 0 && <p className="text-ink/50 font-semibold col-span-full" data-testid="hosts-empty">No hosts yet. The "Meet Your Host" section on trip pages stays hidden until you add one.</p>}
        {hosts.map((h) => (
          <div key={h.id} className="bg-white border border-black/10 p-6" data-testid={`admin-host-${h.id}`}>
            {h.image && <img src={h.image} alt={h.name} className="w-16 h-16 object-cover border border-black/10 mb-4" />}
            <p className="font-bold">{h.name}</p>
            {h.social && <p className="text-xs text-flame font-semibold">{h.social}</p>}
            <p className="text-xs text-ink/60 mt-2 line-clamp-3">{h.bio}</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setEditing(h); setForm({ name: h.name, bio: h.bio, image: h.image, social: h.social }); setDialogOpen(true); }}
                data-testid={`admin-edit-host-${h.id}`} className="p-2 text-ink/50 hover:text-ink" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
              <button onClick={async () => { await api.delete(`/admin/hosts/${h.id}`); load(); }}
                data-testid={`admin-delete-host-${h.id}`} className="p-2 text-ink/50 hover:text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="admin-host-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{editing ? "Edit Host" : "Add Host"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-4" data-testid="admin-host-form">
            <Field label="Name"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="host-form-name" className="rounded-none" /></Field>
            <Field label="Bio"><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="host-form-bio" className="rounded-none min-h-[90px]" /></Field>
            <Field label="Photo URL"><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="host-form-image" className="rounded-none" /></Field>
            <Field label="Social handle"><Input value={form.social} onChange={(e) => setForm({ ...form, social: e.target.value })} data-testid="host-form-social" className="rounded-none" placeholder="@handle" /></Field>
            <Button type="submit" data-testid="host-form-save-btn" className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">Save Host</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", trip: "", quote: "", image: "", handle: "" });

  const load = () => api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/testimonials", form);
      toast.success("Story added");
      setDialogOpen(false);
      setForm({ first_name: "", trip: "", quote: "", image: "", handle: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-6" data-testid="admin-testimonials-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm font-semibold text-ink/60">{items.length} stories — the public section appears automatically once you add the first real one</p>
        <Button onClick={() => setDialogOpen(true)} data-testid="admin-add-testimonial-btn"
          className="bg-flame hover:bg-flame/90 text-white rounded-none font-bold uppercase tracking-widest text-xs">
          <Plus className="w-4 h-4 mr-2" /> Add Story
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.length === 0 && <p className="text-ink/50 font-semibold col-span-full" data-testid="testimonials-empty">No stories yet. Only add real quotes from real Roamies.</p>}
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-black/10 p-6" data-testid={`admin-testimonial-${t.id}`}>
            <p className="text-sm text-ink/75">“{t.quote}”</p>
            <p className="font-bold text-sm mt-3">{t.first_name}{t.handle ? <span className="text-flame"> · {t.handle}</span> : null}</p>
            {t.trip && <p className="text-xs text-ink/50 uppercase tracking-widest mt-1">{t.trip}</p>}
            <button onClick={async () => { await api.delete(`/admin/testimonials/${t.id}`); load(); }}
              data-testid={`admin-delete-testimonial-${t.id}`} className="p-2 mt-3 text-ink/50 hover:text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="admin-testimonial-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Add Traveler Story</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-4" data-testid="admin-testimonial-form">
            <Field label="First name"><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="testimonial-form-name" className="rounded-none" /></Field>
            <Field label="Trip attended"><Input value={form.trip} onChange={(e) => setForm({ ...form, trip: e.target.value })} data-testid="testimonial-form-trip" className="rounded-none" placeholder="Smokies After Dark" /></Field>
            <Field label="Quote"><Textarea required value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="testimonial-form-quote" className="rounded-none min-h-[90px]" /></Field>
            <Field label="Photo URL"><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="testimonial-form-image" className="rounded-none" /></Field>
            <Field label="Instagram/TikTok handle"><Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} data-testid="testimonial-form-handle" className="rounded-none" placeholder="@handle" /></Field>
            <Button type="submit" data-testid="testimonial-form-save-btn" className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">Add Story</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VotesTab() {
  const [votes, setVotes] = useState([]);
  useEffect(() => { api.get("/admin/votes").then((r) => setVotes(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  return (
    <div className="mt-6" data-testid="admin-votes-tab">
      <p className="text-sm font-semibold text-ink/60 mb-6">Destination votes from the Coming Next section — use these to decide the next drop</p>
      <div className="bg-white border border-black/10">
        {votes.length === 0 && <p className="p-8 text-center text-ink/50 font-semibold" data-testid="votes-empty">No votes yet.</p>}
        {votes.map((v) => (
          <div key={v.trip_slug} className="flex items-center justify-between px-6 py-4 border-b border-black/5 text-sm" data-testid={`admin-vote-${v.trip_slug}`}>
            <div>
              <p className="font-bold">{v.title}</p>
              <p className="text-xs text-ink/50">{v.destination}</p>
            </div>
            <span className="font-display text-2xl">{v.votes}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BookingsTab() {
  return (
    <div className="mt-6" data-testid="admin-bookings-tab">
      <div className="bg-white border border-black/10 p-10 max-w-xl">
        <p className="font-display text-3xl uppercase">No bookings yet</p>
        <p className="mt-3 text-sm text-ink/65 leading-relaxed">
          Roamies is waitlist-first right now — no payments are taken on the site, so there's no booking data to show.
          When payments launch, real bookings and revenue will appear here. Nothing fake, ever.
        </p>
      </div>
    </div>
  );
}

function ContactsTab() {
  const [entries, setEntries] = useState([]);
  const load = () => api.get("/admin/contacts").then((r) => setEntries(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="mt-6" data-testid="admin-contacts-tab">
      <p className="text-sm font-semibold text-ink/60 mb-6">{entries.length} messages</p>
      <div className="space-y-4">
        {entries.length === 0 && (
          <p className="p-8 text-center text-ink/50 font-semibold bg-white border border-black/10" data-testid="contacts-empty">No contact messages yet.</p>
        )}
        {entries.map((e) => (
          <div key={e.id} className="bg-white border border-black/10 p-6" data-testid={`admin-contact-row-${e.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-bold">{e.name} <span className="font-normal text-ink/50">· {e.email}</span></p>
                {e.subject && <p className="text-sm font-semibold text-flame mt-1">{e.subject}</p>}
                <p className="text-sm text-ink/70 mt-3 whitespace-pre-wrap">{e.message}</p>
                <p className="text-xs text-ink/40 mt-3">{e.created_at?.slice(0, 10)}</p>
              </div>
              <button onClick={async () => { await api.delete(`/admin/contacts/${e.id}`); load(); }}
                data-testid={`admin-delete-contact-${e.id}`} className="p-2 text-ink/50 hover:text-red-600" aria-label="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubscribersTab() {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    api.get("/admin/subscribers").then((r) => setEntries(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <div className="mt-6" data-testid="admin-subscribers-tab">
      <p className="text-sm font-semibold text-ink/60 mb-6">{entries.length} newsletter subscribers</p>
      <div className="bg-white border border-black/10">
        {entries.length === 0 && <p className="p-8 text-center text-ink/50 font-semibold" data-testid="subscribers-empty">No subscribers yet.</p>}
        {entries.map((e) => (
          <div key={e.id} className="flex items-center justify-between px-6 py-4 border-b border-black/5 text-sm" data-testid={`admin-subscriber-row-${e.id}`}>
            <span className="font-semibold">{e.email}</span>
            <span className="text-xs text-ink/40">{e.created_at?.slice(0, 10)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  return (
    <div className="mt-6 space-y-8" data-testid="admin-settings-tab">
      <RoamieSettingsCard />
      <AdminEmailCard />
      <ChangePasswordCard />
    </div>
  );
}

function RoamieSettingsCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/roamie").then((r) => setForm({
      enabled: r.data.enabled !== false,
      welcome_message: r.data.welcome_message || "",
      quick_actions: (r.data.quick_actions || []).join("\n"),
      faqs: (r.data.faqs || []).map((f) => `${f.q} = ${f.a}`).join("\n"),
      handoff_whatsapp: r.data.handoff_whatsapp || "",
      handoff_email: r.data.handoff_email || "",
      handoff_phone: r.data.handoff_phone || "",
    })).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings/roamie", {
        enabled: form.enabled,
        welcome_message: form.welcome_message,
        quick_actions: lines(form.quick_actions),
        faqs: parseFaqs(form.faqs),
        handoff_whatsapp: form.handoff_whatsapp,
        handoff_email: form.handoff_email,
        handoff_phone: form.handoff_phone,
      });
      toast.success("Roamie settings saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <p className="text-ink/50 font-semibold" data-testid="roamie-settings-loading">Loading Roamie settings...</p>;

  return (
    <div className="max-w-2xl bg-white border border-black/10 p-8" data-testid="roamie-settings-card">
      <p className="font-display text-2xl uppercase mb-2">Roamie Concierge</p>
      <p className="text-sm text-ink/60 mb-6">
        Control Roamie's welcome, quick actions and approved knowledge. Roamie answers only from this approved content
        plus live trip data — anything else goes to the human team.
      </p>
      <form onSubmit={save} className="space-y-5" data-testid="roamie-settings-form">
        <div className="flex items-center justify-between border border-ink/15 p-4">
          <div>
            <p className="text-sm font-bold">Roamie enabled</p>
            <p className="text-xs text-ink/50">Turn off to hide the concierge from the website.</p>
          </div>
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="roamie-enabled-switch" />
        </div>
        <Field label="Welcome message">
          <Textarea rows={3} value={form.welcome_message} onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
            data-testid="roamie-welcome-input" className="rounded-none" />
        </Field>
        <Field label="Quick actions (one per line)">
          <Textarea rows={5} value={form.quick_actions} onChange={(e) => setForm({ ...form, quick_actions: e.target.value })}
            data-testid="roamie-quick-actions-input" className="rounded-none" />
        </Field>
        <Field label="Approved FAQ knowledge (Question = Answer, one per line)">
          <Textarea rows={8} value={form.faqs} onChange={(e) => setForm({ ...form, faqs: e.target.value })}
            data-testid="roamie-faqs-input" className="rounded-none" />
        </Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Handoff WhatsApp">
            <Input value={form.handoff_whatsapp} onChange={(e) => setForm({ ...form, handoff_whatsapp: e.target.value })}
              data-testid="roamie-handoff-whatsapp" className="rounded-none h-12 border-ink/20" />
          </Field>
          <Field label="Handoff email">
            <Input value={form.handoff_email} onChange={(e) => setForm({ ...form, handoff_email: e.target.value })}
              data-testid="roamie-handoff-email" className="rounded-none h-12 border-ink/20" />
          </Field>
          <Field label="Handoff phone">
            <Input value={form.handoff_phone} onChange={(e) => setForm({ ...form, handoff_phone: e.target.value })}
              data-testid="roamie-handoff-phone" className="rounded-none h-12 border-ink/20" />
          </Field>
        </div>
        <Button type="submit" disabled={saving} data-testid="roamie-settings-save-btn"
          className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">
          {saving ? "Saving..." : "Save Roamie Settings"}
        </Button>
      </form>
    </div>
  );
}

function AdminEmailCard() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ new_email: "", current_password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/change-email", form);
      toast.success("Admin email updated. Log in again with your new email.");
      localStorage.removeItem("roamies_admin_token");
      navigate("/admin/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md bg-white border border-black/10 p-8" data-testid="admin-email-card">
      <p className="font-display text-2xl uppercase mb-2">Admin Email</p>
      <p className="text-sm text-ink/60 mb-6">Change the email you use to log in. You'll log in again with the new email right after.</p>
      <form onSubmit={submit} className="space-y-5" data-testid="change-email-form">
        <Field label="New admin email">
          <Input type="email" required value={form.new_email} onChange={(e) => setForm({ ...form, new_email: e.target.value })}
            data-testid="change-email-new-input" className="rounded-none h-12 border-ink/20" />
        </Field>
        <Field label="Current password (to confirm it's you)">
          <Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            data-testid="change-email-password-input" className="rounded-none h-12 border-ink/20" />
        </Field>
        <Button type="submit" disabled={saving} data-testid="change-email-submit-btn"
          className="w-full bg-ink hover:bg-ink/90 text-cream rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">
          {saving ? "Updating..." : "Update Email"}
        </Button>
      </form>
    </div>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("New passwords don't match"); return; }
    if (form.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated. Use it next time you log in.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md bg-white border border-black/10 p-8" data-testid="admin-password-card">
      <p className="font-display text-2xl uppercase mb-2">Change Password</p>
      <p className="text-sm text-ink/60 mb-6">Tip: preview and live site have separate databases — change it where you use it.</p>
      <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
        <Field label="Current password">
          <Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            data-testid="change-password-current" className="rounded-none h-12 border-ink/20" />
        </Field>
        <Field label="New password">
          <Input type="password" required minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            data-testid="change-password-new" className="rounded-none h-12 border-ink/20" />
        </Field>
        <Field label="Confirm new password">
          <Input type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            data-testid="change-password-confirm" className="rounded-none h-12 border-ink/20" />
        </Field>
        <Button type="submit" disabled={saving} data-testid="change-password-submit-btn"
          className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">
          {saving ? "Updating..." : "Update Password"}
        </Button>
      </form>
    </div>
  );
}

const APP_STATUS_BADGE = {
  submitted: "bg-ink/5 text-ink/60",
  ready_for_screening: "bg-flame text-white",
  screening_initiated: "bg-flame text-white",
  screening_review: "bg-flame text-white",
  manual_review: "bg-flame text-white",
  approved: "bg-ink text-cream",
  declined: "bg-ink/5 text-ink/40",
  payment_pending: "bg-ink text-cream",
  booking_confirmed: "bg-ink text-cream",
};

const AppStatusBadge = ({ status }) => (
  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${APP_STATUS_BADGE[status] || "bg-ink/5 text-ink/60"}`}>
    {(status || "submitted").replace(/_/g, " ")}
  </span>
);

function ApplicationDialog({ appId, open, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [provider, setProvider] = useState({ connected: false, provider_name: "" });
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get(`/admin/applications/${appId}`).then((r) => setDetail(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/screening/provider-status").then((r) => setProvider(r.data)).catch(() => {});
  };
  useEffect(() => { if (open && appId) load(); }, [open, appId]);

  const decision = async (status) => {
    setBusy(status);
    try {
      await api.post(`/admin/applications/${appId}/decision`, { status, review_notes: notes });
      toast.success(`Application ${status.replace(/_/g, " ")}`);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const startScreening = async () => {
    setBusy("screening");
    try {
      await api.post(`/admin/applications/${appId}/start-screening`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const saveScreening = async () => {
    setBusy("record");
    try {
      const s = detail.screening || {};
      await api.put(`/admin/screenings/${appId}`, {
        provider: s.provider || "", provider_candidate_id: s.provider_candidate_id || "",
        screening_reference_id: s.screening_reference_id || "", request_id: s.request_id || "",
        status: s.status || "not_started", review_status: s.review_status || "",
        reviewed_by: s.reviewed_by || "", review_date: s.review_date || "", notes: s.notes || "",
      });
      toast.success("Screening record saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const setScr = (k, v) => setDetail((d) => ({ ...d, screening: { ...(d.screening || {}), [k]: v } }));

  const infoRow = (label, value) => (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">{label}</p>
      <p className="text-sm font-semibold mt-0.5">{value || "—"}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="application-dialog">
        {!detail ? (
          <p className="p-6 text-ink/50 font-semibold">Loading application...</p>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl uppercase">
                {detail.first_name} {detail.middle_name} {detail.last_name}
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4" data-testid="application-profile">
              {infoRow("Trip", detail.trip_title)}
              {infoRow("Application ID", detail.id.slice(0, 8))}
              {infoRow("Date of birth", detail.dob)}
              {infoRow("Email", detail.email)}
              {infoRow("Phone", detail.phone)}
              {infoRow("Location", [detail.city, detail.state, detail.country].filter(Boolean).join(", "))}
              {infoRow("Emergency contact", [detail.emergency_contact_name, detail.emergency_contact_relationship && `(${detail.emergency_contact_relationship})`, detail.emergency_contact_phone].filter(Boolean).join(" ") || "Not provided")}
              {infoRow("Application status", "")}
              <div><AppStatusBadge status={detail.status} /></div>
            </div>

            {/* BACKGROUND SCREENING */}
            <div className="mt-8 border border-black/10" data-testid="background-screening-section">
              <div className="bg-ink text-cream px-5 py-3 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.2em]">Background Screening</p>
                <span className="text-[10px] font-bold uppercase tracking-widest bg-flame text-white px-2 py-1" data-testid="provider-status-badge">
                  {provider.connected ? `Provider: ${provider.provider_name}` : "Screening provider — not connected"}
                </span>
              </div>
              <div className="p-5 space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Consent</p>
                    <p className="text-sm font-semibold mt-0.5" data-testid="screening-consent-status">
                      {detail.consent?.given ? `Yes — ${detail.consent.at?.slice(0, 16).replace("T", " ")}` : "Not given"}
                    </p>
                    {detail.consent?.given && (
                      <p className="text-[10px] text-ink/40 mt-1">{detail.consent.disclosure_version} / {detail.consent.authorization_version}</p>
                    )}
                    <span className="inline-block mt-1 text-[9px] font-bold uppercase tracking-widest bg-flame/15 text-flame px-2 py-0.5" data-testid="consent-draft-badge">
                      Draft — owner/legal review required
                    </span>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Screening status</p>
                    <div className="mt-0.5"><ScreeningBadge status={detail.screening?.status} /></div>
                  </div>
                  {infoRow("Initiated", detail.screening?.initiated_at || "—")}
                </div>

                <button
                  onClick={startScreening}
                  disabled={!detail.consent?.given || busy === "screening"}
                  data-testid="start-background-check-btn"
                  className="bg-ink text-cream h-11 px-6 text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40 hover:bg-flame transition-colors"
                >
                  Start Background Check
                </button>
                {!detail.consent?.given && (
                  <p className="text-xs text-ink/50 -mt-3">Disabled until the traveler completes the consent step (emailed to them automatically).</p>
                )}

                <div className="grid grid-cols-2 gap-4 border-t border-black/10 pt-5">
                  <Field label="Provider name"><Input value={detail.screening?.provider || ""} onChange={(e) => setScr("provider", e.target.value)} data-testid="scr-provider" className="rounded-none" /></Field>
                  <Field label="Provider candidate ID"><Input value={detail.screening?.provider_candidate_id || ""} onChange={(e) => setScr("provider_candidate_id", e.target.value)} data-testid="scr-candidate" className="rounded-none" /></Field>
                  <Field label="Screening / report reference ID"><Input value={detail.screening?.screening_reference_id || ""} onChange={(e) => setScr("screening_reference_id", e.target.value)} data-testid="scr-reference" className="rounded-none" /></Field>
                  <Field label="Screening request ID"><Input value={detail.screening?.request_id || ""} onChange={(e) => setScr("request_id", e.target.value)} data-testid="scr-request" className="rounded-none" /></Field>
                  <Field label="Status">
                    <Select value={detail.screening?.status || "not_started"} onValueChange={(v) => setScr("status", v)}>
                      <SelectTrigger data-testid="scr-status" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["not_started", "consent_required", "ready_for_screening", "invitation_sent", "pending", "complete", "review_required", "cleared", "unable_to_verify", "declined"].map((s) => (
                          <SelectItem key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Reviewed by"><Input value={detail.screening?.reviewed_by || ""} onChange={(e) => setScr("reviewed_by", e.target.value)} data-testid="scr-reviewed-by" className="rounded-none" /></Field>
                </div>
                <Field label="Internal notes (never public)">
                  <Textarea value={detail.screening?.notes || ""} onChange={(e) => setScr("notes", e.target.value)} data-testid="scr-notes" className="rounded-none min-h-[70px]" />
                </Field>
                {detail.screening?.screening_reference_id && (
                  <p className="text-xs text-ink/50">Full report stays in the provider's secure dashboard — use "View report with provider" there.</p>
                )}
                <Button onClick={saveScreening} disabled={busy === "record"} data-testid="scr-save-btn"
                  className="bg-ink hover:bg-ink/90 text-cream rounded-none h-10 px-6 font-bold uppercase tracking-widest text-xs">
                  {busy === "record" ? "Saving..." : "Save Screening Record"}
                </Button>
              </div>
            </div>

            {/* DECISION */}
            <div className="mt-6 border border-black/10 p-5" data-testid="application-decision-section">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50 mb-4">Admin decision</p>
              <Field label="Review notes">
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="decision-notes" className="rounded-none min-h-[60px]" />
              </Field>
              <div className="flex flex-wrap gap-3 mt-4">
                <Button onClick={() => decision("approved")} disabled={!!busy} data-testid="decision-approve-btn"
                  className="bg-flame hover:bg-flame/90 text-white rounded-none h-10 px-5 font-bold uppercase tracking-widest text-xs">Approve</Button>
                <Button onClick={() => decision("manual_review")} disabled={!!busy} data-testid="decision-review-btn"
                  className="bg-white border border-ink/20 text-ink hover:border-ink rounded-none h-10 px-5 font-bold uppercase tracking-widest text-xs">Manual review</Button>
                <Button onClick={() => decision("declined")} disabled={!!busy} data-testid="decision-decline-btn"
                  className="bg-white border border-ink/20 text-ink/60 hover:text-red-600 hover:border-red-600 rounded-none h-10 px-5 font-bold uppercase tracking-widest text-xs">Decline</Button>
              </div>
              <p className="text-[11px] text-ink/45 mt-3">Payment (deposit / full) activates only after Approve — and only once payments are connected.</p>
            </div>

            {/* AUDIT LOG */}
            <div className="mt-6" data-testid="application-audit-log">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50 mb-3">Audit log</p>
              <div className="border border-black/10 divide-y divide-black/5 max-h-48 overflow-y-auto">
                {(detail.audit || []).map((a) => (
                  <div key={a.id} className="px-4 py-2.5 text-xs flex justify-between gap-4">
                    <span><span className="font-bold">{a.action.replace(/_/g, " ")}</span>{a.detail ? ` — ${a.detail}` : ""} <span className="text-ink/40">by {a.actor}</span></span>
                    <span className="text-ink/40 shrink-0">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get("/admin/applications").then((r) => setApps(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="mt-6" data-testid="admin-applications-tab">
      <p className="text-sm font-semibold text-ink/60 mb-6">
        {apps.length} applications — applications open when a trip is set to Booking Open
      </p>
      <div className="bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left">
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Applicant</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Trip</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Consent</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Status</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Applied</th>
            </tr>
          </thead>
          <tbody>
            {apps.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-ink/50 font-semibold" data-testid="applications-empty">
                No applications yet — they appear when a trip is in Booking Open status.
              </td></tr>
            )}
            {apps.map((a) => (
              <tr key={a.id} onClick={() => setOpenId(a.id)} className="border-b border-black/5 hover:bg-cream/60 cursor-pointer" data-testid={`admin-application-row-${a.id}`}>
                <td className="p-4 font-bold">{a.first_name} {a.last_name}<span className="block text-xs font-normal text-ink/50">{a.email}</span></td>
                <td className="p-4 text-xs">{a.trip_title}</td>
                <td className="p-4 text-xs">{a.consent?.given ? "✓ Given" : "Pending"}</td>
                <td className="p-4"><AppStatusBadge status={a.status} /></td>
                <td className="p-4 text-xs text-ink/50">{a.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ApplicationDialog appId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}

function TravelerDialog({ email, open, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [provider, setProvider] = useState({ connected: false, provider_name: "" });
  const [busy, setBusy] = useState("");
  const [startError, setStartError] = useState("");
  const [legacy, setLegacy] = useState(null);
  const [decNotes, setDecNotes] = useState({});

  const load = () => {
    if (!email) return;
    api.get(`/admin/travelers/${encodeURIComponent(email)}`)
      .then((r) => { setDetail(r.data); setLegacy(r.data.legacy_screening ? { ...r.data.legacy_screening } : null); })
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/screening/provider-status").then((r) => setProvider(r.data)).catch(() => {});
  };
  useEffect(() => { if (open) { setDetail(null); setStartError(""); load(); } }, [open, email]);

  const setAppScr = (appId, k, v) =>
    setDetail((d) => ({ ...d, applications: d.applications.map((a) => a.id === appId ? { ...a, screening: { ...(a.screening || {}), [k]: v } } : a) }));

  const saveScreening = async (app) => {
    setBusy("record:" + app.id);
    try {
      const s = app.screening || {};
      await api.put(`/admin/screenings/${app.id}`, {
        provider: s.provider || "", provider_candidate_id: s.provider_candidate_id || "",
        screening_reference_id: s.screening_reference_id || "", request_id: s.request_id || "",
        status: s.status || "not_started", review_status: s.review_status || "",
        reviewed_by: s.reviewed_by || "", review_date: s.review_date || "", notes: s.notes || "",
      });
      toast.success("Screening record saved");
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const startScreening = async (app) => {
    setBusy("screening:" + app.id);
    setStartError("");
    try {
      await api.post(`/admin/applications/${app.id}/start-screening`);
      load();
    } catch (err) {
      const msg = formatApiError(err);
      setStartError(msg);
      toast.error(msg);
    } finally {
      setBusy("");
    }
  };

  const decision = async (app, status) => {
    setBusy(status + ":" + app.id);
    try {
      await api.post(`/admin/applications/${app.id}/decision`, { status, review_notes: decNotes[app.id] || "" });
      toast.success(`Application ${status.replace(/_/g, " ")}`);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const saveLegacy = async () => {
    setBusy("legacy");
    try {
      await api.put(`/admin/screening/${encodeURIComponent(email)}`, {
        consent: legacy.consent || false, consent_at: legacy.consent_at || "",
        provider: legacy.provider || "", reference_id: legacy.reference_id || "",
        status: legacy.status || "not_requested", review_required: legacy.review_required || false,
        reviewed_by: legacy.reviewed_by || "", review_date: legacy.review_date || "", notes: legacy.notes || "",
      });
      toast.success("Screening notes saved");
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const infoRow = (label, value) => (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">{label}</p>
      <p className="text-sm font-semibold mt-0.5 break-words">{value || "—"}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="traveler-dialog">
        {!detail ? (
          <p className="p-6 text-ink/50 font-semibold">Loading traveler...</p>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl uppercase" data-testid="traveler-dialog-name">{detail.name || detail.email}</DialogTitle>
              <p className="text-xs text-ink/50">{detail.email}</p>
            </DialogHeader>

            {/* PROFILE */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4" data-testid="traveler-profile-grid">
              {infoRow("Phone", detail.phone)}
              {infoRow("Age", detail.age)}
              {infoRow("Location", [detail.city, detail.state, detail.country].filter(Boolean).join(", "))}
              {infoRow("Instagram", detail.instagram)}
              {infoRow("Heard about us", detail.source)}
              {infoRow("First seen", detail.first_seen?.slice(0, 10))}
            </div>

            {/* HISTORY */}
            <div className="mt-8" data-testid="traveler-history">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50 mb-3">Trip & application history</p>
              <div className="border border-black/10 divide-y divide-black/5">
                {detail.waitlist_history.length === 0 && detail.applications.length === 0 && (
                  <p className="px-4 py-3 text-xs text-ink/50">No trip activity yet.</p>
                )}
                {detail.applications.map((a) => (
                  <div key={a.id} className="px-4 py-3 text-xs flex items-center justify-between gap-4" data-testid={`traveler-history-app-${a.id.slice(0, 8)}`}>
                    <span><span className="font-bold">Applied</span> — {a.trip_title}</span>
                    <span className="flex items-center gap-3 shrink-0"><AppStatusBadge status={a.status} /><span className="text-ink/40">{a.created_at?.slice(0, 10)}</span></span>
                  </div>
                ))}
                {detail.waitlist_history.map((w, i) => (
                  <div key={i} className="px-4 py-3 text-xs flex items-center justify-between gap-4" data-testid={`traveler-history-waitlist-${i}`}>
                    <span><span className="font-bold">Waitlist</span>{w.trip_title ? ` — ${w.trip_title}` : ""}</span>
                    <span className="text-ink/40 shrink-0">{w.created_at?.slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CONSENT SUMMARY */}
            <div className="mt-8" data-testid="traveler-consent-summary">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50 mb-3">Screening consent</p>
              {detail.applications.length === 0 ? (
                <p className="text-xs text-ink/50">No application on file — consent is collected automatically when this traveler applies to a booking-open trip.</p>
              ) : (
                <div className="border border-black/10 divide-y divide-black/5">
                  {detail.applications.map((a) => (
                    <div key={a.id} className="px-4 py-3 text-xs flex items-center justify-between gap-4" data-testid={`traveler-consent-${a.id.slice(0, 8)}`}>
                      <span>{a.trip_title}</span>
                      {a.consent?.given ? (
                        <span className="font-bold">Given — {a.consent.at?.slice(0, 16).replace("T", " ")} <span className="font-normal text-ink/40">({a.consent.disclosure_version})</span> <span className="ml-1 text-[9px] font-bold uppercase tracking-widest bg-flame/15 text-flame px-1.5 py-0.5">Draft — legal review</span></span>
                      ) : (
                        <span className="text-ink/50 font-semibold">Not given — consent link emailed</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* BACKGROUND SCREENING */}
            <div className="mt-8 border border-black/10" data-testid="traveler-screening-section">
              <div className="bg-ink text-cream px-5 py-3 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.2em]">Background Screening</p>
                <span className="text-[10px] font-bold uppercase tracking-widest bg-flame text-white px-2 py-1" data-testid="traveler-provider-badge">
                  {provider.connected ? `Provider: ${provider.provider_name}` : "Screening provider — not connected"}
                </span>
              </div>
              <div className="p-5 space-y-8">
                {startError && (
                  <div className="bg-flame/10 border border-flame/40 p-4 text-xs font-bold text-ink" data-testid="traveler-start-error">
                    {startError}
                  </div>
                )}

                {detail.applications.length === 0 && (
                  <div data-testid="traveler-legacy-screening">
                    <p className="text-xs text-ink/60 leading-relaxed mb-4">
                      This traveler hasn't submitted a trip application yet. Background checks start from an application
                      with signed consent — nothing can be initiated here.
                    </p>
                    <button disabled data-testid="traveler-start-check-disabled"
                      className="bg-ink text-cream h-11 px-6 text-xs font-bold uppercase tracking-[0.2em] opacity-40 cursor-not-allowed">
                      Start Background Check
                    </button>
                    <p className="text-xs text-ink/50 mt-2">Disabled — requires a trip application with signed screening consent.</p>
                    {legacy && (
                      <div className="mt-6 border-t border-black/10 pt-5 space-y-4">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Manual screening record (admin only)</p>
                        <div className="grid grid-cols-2 gap-4">
                          <Field label="Status">
                            <Select value={legacy.status || "not_requested"} onValueChange={(v) => setLegacy({ ...legacy, status: v })}>
                              <SelectTrigger data-testid="legacy-scr-status" className="rounded-none"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {SCREENING_STATUSES.map((s) => (
                                  <SelectItem key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Field>
                          <Field label="Provider"><Input value={legacy.provider || ""} onChange={(e) => setLegacy({ ...legacy, provider: e.target.value })} data-testid="legacy-scr-provider" className="rounded-none" /></Field>
                          <Field label="Reference ID"><Input value={legacy.reference_id || ""} onChange={(e) => setLegacy({ ...legacy, reference_id: e.target.value })} data-testid="legacy-scr-reference" className="rounded-none" /></Field>
                          <Field label="Reviewed by"><Input value={legacy.reviewed_by || ""} onChange={(e) => setLegacy({ ...legacy, reviewed_by: e.target.value })} data-testid="legacy-scr-reviewed-by" className="rounded-none" /></Field>
                        </div>
                        <Field label="Internal notes (never public)">
                          <Textarea value={legacy.notes || ""} onChange={(e) => setLegacy({ ...legacy, notes: e.target.value })} data-testid="legacy-scr-notes" className="rounded-none min-h-[70px]" />
                        </Field>
                        <Button onClick={saveLegacy} disabled={busy === "legacy"} data-testid="legacy-scr-save-btn"
                          className="bg-ink hover:bg-ink/90 text-cream rounded-none h-10 px-6 font-bold uppercase tracking-widest text-xs">
                          {busy === "legacy" ? "Saving..." : "Save Screening Record"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {detail.applications.map((a) => (
                  <div key={a.id} className="border border-black/10" data-testid={`traveler-screening-app-${a.id.slice(0, 8)}`}>
                    <div className="px-5 py-3 border-b border-black/10 flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-xs font-bold uppercase tracking-widest">{a.trip_title} <span className="text-ink/40 font-normal">· app {a.id.slice(0, 8)}</span></p>
                      <AppStatusBadge status={a.status} />
                    </div>
                    <div className="p-5 space-y-5">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Consent</p>
                          <p className="text-sm font-semibold mt-0.5" data-testid={`traveler-consent-status-${a.id.slice(0, 8)}`}>
                            {a.consent?.given ? `Yes — ${a.consent.at?.slice(0, 16).replace("T", " ")}` : "Not given"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">Screening status</p>
                          <div className="mt-0.5"><ScreeningBadge status={a.screening?.status} /></div>
                        </div>
                        {infoRow("Review status", a.screening?.review_status)}
                        {infoRow("Initiated", a.screening?.initiated_at?.slice(0, 16).replace("T", " "))}
                        {infoRow("Last updated", a.screening?.updated_at?.slice(0, 16).replace("T", " "))}
                        {infoRow("Review date", a.screening?.review_date?.slice(0, 16).replace("T", " "))}
                      </div>

                      <div>
                        <button onClick={() => startScreening(a)} disabled={!a.consent?.given || busy === "screening:" + a.id}
                          data-testid={`traveler-start-check-${a.id.slice(0, 8)}`}
                          className="bg-ink text-cream h-11 px-6 text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40 hover:bg-flame transition-colors">
                          {busy === "screening:" + a.id ? "Starting..." : "Start Background Check"}
                        </button>
                        {!a.consent?.given && (
                          <p className="text-xs text-ink/50 mt-2">Disabled until the traveler completes the consent step (link emailed to them automatically).</p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4 border-t border-black/10 pt-5">
                        <Field label="Provider name"><Input value={a.screening?.provider || ""} onChange={(e) => setAppScr(a.id, "provider", e.target.value)} data-testid={`tscr-provider-${a.id.slice(0, 8)}`} className="rounded-none" /></Field>
                        <Field label="Provider candidate ID"><Input value={a.screening?.provider_candidate_id || ""} onChange={(e) => setAppScr(a.id, "provider_candidate_id", e.target.value)} data-testid={`tscr-candidate-${a.id.slice(0, 8)}`} className="rounded-none" /></Field>
                        <Field label="Screening / report reference ID"><Input value={a.screening?.screening_reference_id || ""} onChange={(e) => setAppScr(a.id, "screening_reference_id", e.target.value)} data-testid={`tscr-reference-${a.id.slice(0, 8)}`} className="rounded-none" /></Field>
                        <Field label="Screening request ID"><Input value={a.screening?.request_id || ""} onChange={(e) => setAppScr(a.id, "request_id", e.target.value)} data-testid={`tscr-request-${a.id.slice(0, 8)}`} className="rounded-none" /></Field>
                        <Field label="Status">
                          <Select value={a.screening?.status || "not_started"} onValueChange={(v) => setAppScr(a.id, "status", v)}>
                            <SelectTrigger data-testid={`tscr-status-${a.id.slice(0, 8)}`} className="rounded-none"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {["not_started", "consent_required", "ready_for_screening", "invitation_sent", "pending", "complete", "review_required", "cleared", "unable_to_verify", "declined"].map((s) => (
                                <SelectItem key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Reviewed by"><Input value={a.screening?.reviewed_by || ""} onChange={(e) => setAppScr(a.id, "reviewed_by", e.target.value)} data-testid={`tscr-reviewed-by-${a.id.slice(0, 8)}`} className="rounded-none" /></Field>
                      </div>
                      <Field label="Internal notes (never public)">
                        <Textarea value={a.screening?.notes || ""} onChange={(e) => setAppScr(a.id, "notes", e.target.value)} data-testid={`tscr-notes-${a.id.slice(0, 8)}`} className="rounded-none min-h-[70px]" />
                      </Field>
                      <Button onClick={() => saveScreening(a)} disabled={busy === "record:" + a.id} data-testid={`tscr-save-${a.id.slice(0, 8)}`}
                        className="bg-ink hover:bg-ink/90 text-cream rounded-none h-10 px-6 font-bold uppercase tracking-widest text-xs">
                        {busy === "record:" + a.id ? "Saving..." : "Save Screening Record"}
                      </Button>

                      {/* REVIEW CONTROLS */}
                      <div className="border-t border-black/10 pt-5" data-testid={`traveler-decision-section-${a.id.slice(0, 8)}`}>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45 mb-3">Admin review</p>
                        <Field label="Review notes">
                          <Textarea value={decNotes[a.id] || ""} onChange={(e) => setDecNotes({ ...decNotes, [a.id]: e.target.value })} data-testid={`tdecision-notes-${a.id.slice(0, 8)}`} className="rounded-none min-h-[50px]" />
                        </Field>
                        <div className="flex flex-wrap gap-3 mt-3">
                          <Button onClick={() => decision(a, "approved")} disabled={!!busy} data-testid={`tdecision-approve-${a.id.slice(0, 8)}`}
                            className="bg-flame hover:bg-flame/90 text-white rounded-none h-10 px-5 font-bold uppercase tracking-widest text-xs">Approve</Button>
                          <Button onClick={() => decision(a, "manual_review")} disabled={!!busy} data-testid={`tdecision-review-${a.id.slice(0, 8)}`}
                            className="bg-white border border-ink/20 text-ink hover:border-ink rounded-none h-10 px-5 font-bold uppercase tracking-widest text-xs">Manual review</Button>
                          <Button onClick={() => decision(a, "declined")} disabled={!!busy} data-testid={`tdecision-decline-${a.id.slice(0, 8)}`}
                            className="bg-white border border-ink/20 text-ink/60 hover:text-red-600 hover:border-red-600 rounded-none h-10 px-5 font-bold uppercase tracking-widest text-xs">Decline</Button>
                        </div>
                      </div>

                      {/* PER-APP AUDIT */}
                      <div className="border-t border-black/10 pt-5" data-testid={`traveler-audit-${a.id.slice(0, 8)}`}>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45 mb-3">Audit history</p>
                        <div className="border border-black/10 divide-y divide-black/5 max-h-40 overflow-y-auto">
                          {(a.audit || []).length === 0 && <p className="px-4 py-3 text-xs text-ink/50">No recorded actions yet.</p>}
                          {(a.audit || []).map((log) => (
                            <div key={log.id} className="px-4 py-2.5 text-xs flex justify-between gap-4">
                              <span><span className="font-bold">{log.action.replace(/_/g, " ")}</span>{log.detail ? ` — ${log.detail}` : ""} <span className="text-ink/40">by {log.actor}</span></span>
                              <span className="text-ink/40 shrink-0">{log.created_at?.slice(0, 16).replace("T", " ")}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TravelersTab() {
  const [people, setPeople] = useState([]);
  const [openEmail, setOpenEmail] = useState(null);
  const load = () => api.get("/admin/travelers").then((r) => setPeople(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="mt-6" data-testid="admin-travelers-tab">
      <p className="text-sm font-semibold text-ink/60 mb-6">{people.length} unique travelers (from waitlist signups) — click a row to open the full traveler profile</p>
      <div className="bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left">
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Traveler</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">From</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Age</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Interested in</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Screening</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50 text-right">Profile</th>
            </tr>
          </thead>
          <tbody>
            {people.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-ink/50 font-semibold" data-testid="travelers-empty">No travelers yet.</td></tr>
            )}
            {people.map((p) => (
              <tr key={p.email} onClick={() => setOpenEmail(p.email)} className="border-b border-black/5 hover:bg-cream/60 cursor-pointer" data-testid={`admin-traveler-row-${p.email.replace(/[^a-z0-9]/g, "-")}`}>
                <td className="p-4 font-bold">{p.name}<span className="block text-xs font-normal text-ink/50">{p.email}</span></td>
                <td className="p-4 text-xs">{[p.city, p.country].filter(Boolean).join(", ") || "—"}</td>
                <td className="p-4 text-xs">{p.age || "—"}</td>
                <td className="p-4 text-xs max-w-[220px]">{(p.interests || []).join(", ") || "—"}</td>
                <td className="p-4"><ScreeningBadge status={p.screening?.status} /></td>
                <td className="p-4 text-right">
                  <button onClick={(e) => { e.stopPropagation(); setOpenEmail(p.email); }} data-testid={`traveler-open-${p.email.replace(/[^a-z0-9]/g, "-")}`}
                    className="p-2 text-ink/50 hover:text-flame transition-colors" aria-label={`Open profile for ${p.name}`}><ChevronRight className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TravelerDialog email={openEmail} open={!!openEmail} onClose={() => setOpenEmail(null)} onChanged={load} />
    </div>
  );
}

const SCREENING_STATUSES = ["not_requested", "consent_required", "pending", "complete", "review_required", "cleared", "unable_to_verify"];

const ScreeningBadge = ({ status }) => (
  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${
    status === "cleared" || status === "complete" ? "bg-ink text-cream"
    : status === "review_required" ? "bg-flame text-white"
    : "bg-ink/5 text-ink/60"
  }`} data-testid={`screening-badge-${status || "not_requested"}`}>
    {(status || "not_requested").replace(/_/g, " ")}
  </span>
);

function ScreeningTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [provider, setProvider] = useState({ connected: false });

  const load = () => {
    const params = filter !== "all" ? { status: filter } : {};
    api.get("/admin/screening/dashboard", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/screening/provider-status").then((r) => setProvider(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);

  const filters = ["all", ...SCREENING_STATUSES];

  return (
    <div className="mt-6" data-testid="admin-screening-tab">
      <div className="bg-ink text-cream p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="screening-provider-banner">
        <p className="text-xs font-bold uppercase tracking-[0.2em]">
          Screening provider — {provider.connected ? provider.provider_name : "not connected"}
        </p>
        <p className="text-xs text-cream/60 max-w-md">
          Integration-ready for a legitimate third-party provider. No automated checks run from Roamies; potential matches always require human review.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="screening-filters">
        {filters.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`screening-filter-${f}`}
            className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest border transition-colors ${
              filter === f ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink/60 hover:border-ink"
            }`}>
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left">
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Traveler</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Trip</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">App ID</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">DOB</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Consent</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Provider / ID</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Status</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Review</th>
              <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50 text-right">Open</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-ink/50 font-semibold" data-testid="screening-empty">
                No applications in this state. Applications appear here when a trip is in Booking Open status.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.application_id} className="border-b border-black/5 hover:bg-cream/60" data-testid={`admin-screening-row-${r.application_id.slice(0, 8)}`}>
                <td className="p-4 font-bold">{r.traveler}</td>
                <td className="p-4 text-xs">{r.trip}</td>
                <td className="p-4 text-xs text-ink/50">{r.application_id.slice(0, 8)}</td>
                <td className="p-4 text-xs">{r.dob || "—"}</td>
                <td className="p-4 text-xs">{r.consent_given ? "✓" : "—"}</td>
                <td className="p-4 text-xs">{r.provider ? `${r.provider} · ${r.screening_id || "—"}` : "—"}</td>
                <td className="p-4"><ScreeningBadge status={r.screening_status} /></td>
                <td className="p-4 text-xs">{r.review_status || "—"}</td>
                <td className="p-4 text-right">
                  <button onClick={() => setOpenId(r.application_id)} data-testid={`screening-open-${r.application_id.slice(0, 8)}`}
                    className="p-2 text-ink/50 hover:text-ink" aria-label="Open traveler profile"><Pencil className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ApplicationDialog appId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}

const FINANCE_COPY = {
  payments: { title: "Payments", text: "Total collected, deposits, balances, transaction list, payment statuses, and Stripe reference IDs will live here. No banking or card details are ever stored in the Roamies database." },
  payouts: { title: "Payouts", text: "Payout status and history will live here once Stripe is connected. Nothing is processed until then." },
  refunds: { title: "Refunds", text: "Refund requests and statuses will live here once payments are active. Nothing is processed until then." },
};

function FinanceTab({ kind }) {
  const copy = FINANCE_COPY[kind];
  return (
    <div className="mt-6" data-testid={`admin-${kind}-tab`}>
      <div className="bg-white border border-black/10 p-10 max-w-xl">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] bg-flame text-white px-3 py-1.5">Test mode — not activated</span>
        <p className="font-display text-3xl uppercase mt-5">{copy.title}</p>
        <p className="mt-3 text-sm text-ink/65 leading-relaxed">{copy.text}</p>
        <p className="mt-4 text-xs text-ink/45">Tell me when you're ready and I'll connect Stripe for deposits and payments.</p>
      </div>
    </div>
  );
}

function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ code: "", description: "", active: false });

  const load = () => api.get("/admin/promos").then((r) => setPromos(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/promos", form);
      toast.success("Promo code saved");
      setDialogOpen(false);
      setForm({ code: "", description: "", active: false });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-6" data-testid="admin-promos-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm font-semibold text-ink/60">Codes are stored here but not validated at checkout until payments launch</p>
        <Button onClick={() => setDialogOpen(true)} data-testid="admin-add-promo-btn"
          className="bg-flame hover:bg-flame/90 text-white rounded-none font-bold uppercase tracking-widest text-xs">
          <Plus className="w-4 h-4 mr-2" /> Add Code
        </Button>
      </div>
      <div className="bg-white border border-black/10">
        {promos.length === 0 && <p className="p-8 text-center text-ink/50 font-semibold" data-testid="promos-empty">No promo codes yet.</p>}
        {promos.map((p) => (
          <div key={p.id} className="flex items-center justify-between px-6 py-4 border-b border-black/5 text-sm" data-testid={`admin-promo-${p.code}`}>
            <div>
              <p className="font-display text-xl">{p.code}</p>
              {p.description && <p className="text-xs text-ink/50">{p.description}</p>}
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${p.active ? "bg-ink text-cream" : "bg-ink/5 text-ink/50"}`}>
                {p.active ? "Active" : "Inactive"}
              </span>
              <button onClick={async () => { await api.delete(`/admin/promos/${p.id}`); load(); }}
                data-testid={`admin-delete-promo-${p.code}`} className="p-2 text-ink/50 hover:text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="promo-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Add Promo Code</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-4" data-testid="promo-form">
            <Field label="Code"><Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" className="rounded-none" placeholder="CREW20" /></Field>
            <Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="promo-description-input" className="rounded-none" placeholder="20% off deposit" /></Field>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="promo-active-switch" />
              <Label className="text-xs font-bold uppercase tracking-widest">Active (when payments launch)</Label>
            </div>
            <Button type="submit" data-testid="promo-save-btn" className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">Save Code</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContentTab() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/public").then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings/social", form);
      toast.success("Social & contact settings saved — the website updates instantly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    ["website", "Website URL", "https://roamieslife.com"],
    ["email", "Contact Email", "teamroamies@gmail.com"],
    ["phone", "Phone (Call/Text)", "+1 330-988-5582"],
    ["instagram", "Instagram URL", "https://instagram.com/..."],
    ["facebook", "Facebook URL", "https://facebook.com/..."],
    ["tiktok", "TikTok URL", "https://tiktok.com/@..."],
    ["youtube", "YouTube URL (optional)", "https://youtube.com/@..."],
    ["whatsapp_number", "WhatsApp Number", "+1 330-988-5582"],
    ["whatsapp_channel", "WhatsApp Channel URL", "https://whatsapp.com/channel/..."],
    ["whatsapp_community", "WhatsApp Community URL", "https://chat.whatsapp.com/..."],
  ];

  return (
    <div className="mt-6 max-w-md" data-testid="admin-content-tab">
      <div className="bg-white border border-black/10 p-8">
        <p className="font-display text-2xl uppercase mb-2">Social & Contact Settings</p>
        <p className="text-sm text-ink/60 mb-6">These power the footer icons, the Connect section and the WhatsApp hub across the site — update here, no code changes needed.</p>
        <form onSubmit={save} className="space-y-4" data-testid="content-social-form">
          {fields.map(([key, label, placeholder]) => (
            <Field key={key} label={label}>
              <Input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`social-${key}-input`} className="rounded-none h-12 border-ink/20" placeholder={placeholder} />
            </Field>
          ))}
          <Button type="submit" disabled={saving} data-testid="content-save-btn"
            className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-12 font-bold uppercase tracking-[0.2em] text-xs">
            {saving ? "Saving..." : "Save Social & Contact Settings"}
          </Button>
        </form>
      </div>
    </div>
  );
}


// ---------- SURVEY ----------

const SURVEY_Q_LABELS = {
  q1: "Q1 · Most excited about", q2: "Q2 · Age range", q3: "Q3 · Leisure travel frequency", q4: "Q4 · Usually travels with",
  q5: "Q5 · When friends cancel", q6: "Q6 · What stops more travel (up to 3)", q7: "Q7 · Experiences of interest (up to 3)",
  q8: "Q8 · Ideal getaway length", q9: "Q9 · Reasonable 3–5 day US trip price", q10: "Q10 · Comfort joining strangers (1–5)",
  q11: "Q11 · Comfort factors (multi)", q12: "Q12 · Crew-building ingredients", q13: "Q13 · Top booking factors (up to 3)",
  q14: "Q14 · Why Roamies over DIY (written)", q15: "Q15 · Dream destination (written)", q16: "Q16 · Traveler type",
};

const COMM_LABELS = { marketing: "Yes, keep me in the loop", follow_up_only: "Survey follow-up only", none: "No thanks" };

function SurveyTab() {
  const [view, setView] = useState("responses");
  const [rows, setRows] = useState(null);
  const [insights, setInsights] = useState(null);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadResponses = (q = "") =>
    api.get(`/admin/survey/responses${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));

  const deleteResponse = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/survey/responses/${deleteTarget.id}`);
      setRows((rs) => rs.filter((r) => r.id !== deleteTarget.id));
      setInsights(null); // force recalculation next time Insights is opened
      toast.success("Survey response deleted.");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => { loadResponses(); }, []);
  useEffect(() => {
    if (view === "insights" && !insights) {
      api.get("/admin/survey/insights").then((r) => setInsights(r.data)).catch((e) => toast.error(formatApiError(e)));
    }
  }, [view]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/survey/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "roamies-survey-responses.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openRow = rows?.find((r) => r.id === openId);

  const Bar = ({ label, count, total }) => (
    <div className="flex items-center gap-3 text-xs">
      <span className="w-40 sm:w-52 shrink-0 truncate font-semibold">{label}</span>
      <div className="flex-1 h-2 bg-ink/10">
        <div className="h-full bg-flame" style={{ width: `${total ? Math.round((count / total) * 100) : 0}%` }} />
      </div>
      <span className="w-8 text-right font-bold">{count}</span>
    </div>
  );

  return (
    <div className="mt-6" data-testid="admin-survey-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex gap-2">
          <button onClick={() => setView("responses")} data-testid="survey-view-responses"
            className={`px-5 h-10 text-xs font-bold uppercase tracking-widest border transition-colors ${view === "responses" ? "bg-ink text-cream border-ink" : "bg-white border-ink/20 hover:border-ink"}`}>
            Responses
          </button>
          <button onClick={() => setView("insights")} data-testid="survey-view-insights"
            className={`px-5 h-10 text-xs font-bold uppercase tracking-widest border transition-colors ${view === "insights" ? "bg-ink text-cream border-ink" : "bg-white border-ink/20 hover:border-ink"}`}>
            Insights
          </button>
        </div>
        {view === "responses" && (
          <div className="flex gap-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadResponses(search)}
              placeholder="Search name / email / style..." data-testid="survey-search-input"
              className="rounded-none h-10 w-56 border-ink/20" />
            <Button onClick={() => loadResponses(search)} data-testid="survey-search-btn"
              className="rounded-none h-10 bg-ink text-cream font-bold uppercase tracking-widest text-xs">Search</Button>
            <Button onClick={exportCsv} data-testid="survey-export-btn"
              className="rounded-none h-10 bg-white border border-ink/20 text-ink hover:border-ink font-bold uppercase tracking-widest text-xs">
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
          </div>
        )}
      </div>

      {view === "responses" && (
        <>
          {!rows ? (
            <p className="text-ink/50 font-semibold py-8">Loading responses...</p>
          ) : rows.length === 0 ? (
            <div className="bg-white border border-black/10 p-10 text-center" data-testid="survey-empty">
              <p className="font-display text-2xl uppercase">No survey responses yet.</p>
              <p className="text-sm text-ink/50 mt-2">Share roamieslife.com/survey to start collecting traveler insights.</p>
            </div>
          ) : (
            <div className="bg-white border border-black/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-left">
                    <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Submitted</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Traveler</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Travel style</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Comfort</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50">Comms</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest text-ink/50 text-right">View</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} onClick={() => setOpenId(r.id)} className="border-b border-black/5 hover:bg-cream/60 cursor-pointer"
                      data-testid={`survey-response-row-${r.id.slice(0, 8)}`}>
                      <td className="p-4 text-xs text-ink/60">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="p-4 font-bold">{r.full_name}<span className="block text-xs font-normal text-ink/50">{r.email}</span></td>
                      <td className="p-4 text-xs font-semibold text-flame">{r.personality}</td>
                      <td className="p-4 text-xs">{r.answers?.q10 || "—"}/5</td>
                      <td className="p-4 text-xs">{COMM_LABELS[r.comm_preference] || r.comm_preference}</td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}
                          data-testid={`survey-delete-${r.id.slice(0, 8)}`}
                          className="p-2 text-ink/50 hover:text-red-600 transition-colors" aria-label={`Delete response from ${r.full_name}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <ChevronRight className="w-4 h-4 inline text-ink/40" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {view === "insights" && (
        <div data-testid="survey-insights">
          {!insights ? (
            <p className="text-ink/50 font-semibold py-8">Loading insights...</p>
          ) : insights.total === 0 ? (
            <div className="bg-white border border-black/10 p-10 text-center" data-testid="insights-empty">
              <p className="font-display text-2xl uppercase">No data yet.</p>
              <p className="text-sm text-ink/50 mt-2">Insights appear here once travelers submit the survey.</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  ["Total responses", insights.total, "insights-total"],
                  ["Avg comfort joining strangers", `${insights.comfort_avg ?? "—"} / 5`, "insights-comfort"],
                  ["Dream destinations shared", insights.dream_destinations.length, "insights-dreams"],
                  ["Open to marketing", `${insights.comm_preference?.marketing || 0}`, "insights-marketing"],
                ].map(([label, val, tid]) => (
                  <div key={tid} className="bg-ink text-cream p-5" data-testid={tid}>
                    <p className="text-3xl font-display">{val}</p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-cream/60 mt-1">{label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-black/10 p-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-4">Travel style distribution</p>
                  <div className="space-y-2" data-testid="insights-personality">
                    {Object.entries(insights.personality).map(([k, v]) => <Bar key={k} label={k} count={v} total={insights.total} />)}
                  </div>
                </div>
                <div className="bg-white border border-black/10 p-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-4">Communication preference</p>
                  <div className="space-y-2" data-testid="insights-comm">
                    {Object.entries(insights.comm_preference).map(([k, v]) => <Bar key={k} label={COMM_LABELS[k] || k} count={v} total={insights.total} />)}
                  </div>
                </div>
                {Object.entries(insights.questions).map(([key, q]) => (
                  <div key={key} className="bg-white border border-black/10 p-5" data-testid={`insights-${key}`}>
                    <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-4">{q.label}</p>
                    <div className="space-y-2">
                      {Object.entries(q.counts).map(([k, v]) => (
                        <Bar key={k} label={key === "q16" ? (SURVEY_Q_LABELS[k] || k) : k} count={v}
                          total={["q6", "q7", "q11", "q13"].includes(key) ? Object.values(q.counts).reduce((a, b) => a + b, 0) : insights.total} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-black/10 p-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-4">Dream destinations (Q15)</p>
                  <div className="space-y-2 text-sm" data-testid="insights-dream-list">
                    {insights.dream_destinations.map((d, i) => <p key={i} className="border-b border-black/5 pb-2">{d}</p>)}
                  </div>
                </div>
                <div className="bg-white border border-black/10 p-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mb-4">"Why Roamies over DIY" (Q14)</p>
                  <div className="space-y-2 text-sm" data-testid="insights-why-list">
                    {insights.why_roamies.map((d, i) => <p key={i} className="border-b border-black/5 pb-2">{d}</p>)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DELETE CONFIRMATION */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none" data-testid="survey-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl uppercase">Delete this survey response?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this response{deleteTarget ? ` (${deleteTarget.full_name} — ${deleteTarget.email})` : ""} and its associated survey data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="survey-delete-cancel" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteResponse} disabled={deleting} data-testid="survey-delete-confirm"
              className="rounded-none bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Deleting..." : "Delete Response"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* RESPONSE DETAIL */}
      <Dialog open={!!openId} onOpenChange={() => setOpenId(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="survey-detail-dialog">
          {!openRow ? null : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl uppercase">{openRow.full_name}</DialogTitle>
                <p className="text-xs text-ink/50">Response {openRow.id.slice(0, 8)} · {openRow.created_at?.slice(0, 16).replace("T", " ")} UTC</p>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                {[["Email", openRow.email], ["Mobile", openRow.mobile], ["Instagram", openRow.instagram],
                  ["TikTok", openRow.tiktok], ["Comms", COMM_LABELS[openRow.comm_preference] || openRow.comm_preference],
                  ["Travel style", openRow.personality]].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">{label}</p>
                    <p className="text-sm font-semibold mt-0.5 break-words">{value || "—"}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t border-black/10 pt-5 space-y-4">
                {Object.entries(SURVEY_Q_LABELS).map(([key, label]) => {
                  const v = openRow.answers?.[key];
                  return (
                    <div key={key}>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ink/45">{label}</p>
                      <p className="text-sm font-semibold mt-0.5">{Array.isArray(v) ? v.join(" · ") : (v ?? "—")}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
